import { FunnelIcon } from '@heroicons/react/24/outline';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { SparkAreaChart, SparkBarChart } from '@tremor/react';
import { getYear, subYears } from 'date-fns';
import { useMemo, type ReactNode } from 'react';
import type { CellObject } from 'xlsx';
import { CurrencyOrEmpty } from '../../../components/typography/currency-or-empty.tsx';
import { DataText } from '../../../components/typography/data-text.tsx';
import { NumberOrEmpty } from '../../../components/typography/number-or-empty.tsx';
import { PercentageOrEmpty } from '../../../components/typography/percentage-or-empty.tsx';
import { Button } from '../../../components/ui/button.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { ExplorerGetExplorerTotalSummary } from '../../../server/types/infer-trpc-types.ts';
import type {
    GrossMarginPercentageYearPeriodKey,
    SparklineMonth,
} from '../../../server/utils/explorer-utils.ts';
import {
    BillingStatus,
    getDateRange,
    TargetUnit,
    yearPeriodsHelper,
} from '../../../server/utils/explorer-utils.ts';
import { useAcl } from '../../acl/acl-provider.tsx';
import { MaskedContent } from '../../customer-mode/components/masked-content.tsx';
import { DataTableSortHeader } from '../../list-views/components/data-table-sort-header.tsx';
import { DeltaDoubleDeckerCell } from '../../list-views/components/delta-double-decker-cell.tsx';
import { MetricInfoHint } from '../components/metric-explainer.tsx';
import { useExplorerView } from './use-explorer-query-params.ts';

export type XlsxCellValue = string | number | boolean | Date | CellObject | null;

export type ExplorerTableData = {
    id?: string | null;
    name?: string | null;
    revenue_current: number;
    revenue_last_period: number;
    gross_margin_current: number;
    gross_margin_last_period: number;
    gross_margin_percentage_current: number;
    gross_margin_percentage_last_period: number;
    revenue_delta_amount: number;
    revenue_delta_percentage: number | null;
    gross_margin_delta_amount: number;
    gross_margin_delta_percentage: number | null;
    units_current: number;
    units_last_period: number;
    units_delta_amount: number;
    units_delta_percentage: number | null;
    monthly_sales_over_last_2_years: SparklineMonth[];
} & Record<GrossMarginPercentageYearPeriodKey, number>;

type ConfigurableColumnDefinition<T extends ExplorerTableData> = {
    columns: {
        id: string;
        accessorKey: string;
        headerText: string;
        generateCell: (value: T) => React.ReactNode;
        size?: number;
    }[];
};

type ExplorerColumnDef<T extends ExplorerTableData> = ColumnDef<T> & {
    id: string;
    accessorKey?: string;
    summary?: (data: ExplorerGetExplorerTotalSummary) => React.ReactNode;
    exporter?: {
        name: string;
        getXlsxCell: (data: T) => XlsxCellValue;
    }[];
};

const SPARK_CHART_COLORS = ['tremor-chart-primary'];
const SPARK_AREA_REVENUE_CATEGORIES = ['monthly_sales'];
const SPARK_AREA_UNITS_CATEGORIES = ['monthly_unit_total'];
const SPARK_BAR_ORDER_COUNT_CATEGORIES = ['monthly_order_count'];

// APP-1756: These chart components cannot be inlined in the 'useMemo' present in the columns definition.
// Every time the memo recomputes, the inline arrow functions are recreated with fresh references.
// This causes the cell components to unmount and remount rather than re-render in place, which result in a "flicker" effect.
function RevenueSparkAreaCell<T extends ExplorerTableData>({ row }: CellContext<T, unknown>): ReactNode {
    return (
        <SparkAreaChart
            autoMinValue
            data={row.original.monthly_sales_over_last_2_years}
            index="month_year"
            categories={SPARK_AREA_REVENUE_CATEGORIES}
            colors={SPARK_CHART_COLORS}
            className="mx-auto"
            curveType="monotone"
        />
    );
}

function OrderCountSparkBarCell<T extends ExplorerTableData>({ row }: CellContext<T, unknown>): ReactNode {
    return (
        <SparkBarChart
            data={row.original.monthly_sales_over_last_2_years}
            index="month_year"
            categories={SPARK_BAR_ORDER_COUNT_CATEGORIES}
            colors={SPARK_CHART_COLORS}
            className="mx-auto border-b border-neutral-200"
        />
    );
}

function UnitsSparkAreaCell<T extends ExplorerTableData>({ row }: CellContext<T, unknown>): ReactNode {
    return (
        <SparkAreaChart
            autoMinValue
            data={row.original.monthly_sales_over_last_2_years}
            index="month_year"
            categories={SPARK_AREA_UNITS_CATEGORIES}
            colors={SPARK_CHART_COLORS}
            className="mx-auto"
            curveType="monotone"
        />
    );
}

export function useConfigurableExplorerColumns<T extends ExplorerTableData = ExplorerTableData>(
    config: ConfigurableColumnDefinition<T>,
) {
    const { t } = useLocale('explorer');
    const acl = useAcl();

    const { state } = useExplorerView();
    const { window, yearPeriods, targetUnit, customDateRange, billing } = state;
    // The monetary columns show the same data for every billing status; only the labels differ.
    const periodLabelKey = (
        {
            [BillingStatus.BILLED]: 'table_label__revenue_period',
            [BillingStatus.BILLED_WO_CREDIT_NOTE]: 'table_label__billed_wo_credit_note_period',
            [BillingStatus.NOT_BILLED]: 'table_label__unbilled_period',
            [BillingStatus.ORDER_SUM]: 'table_label__order_sum_period',
        } as const
    )[billing];
    const sparkChartLabelKey = (
        {
            [BillingStatus.BILLED]: 'table_label__revenue_spark_chart',
            [BillingStatus.BILLED_WO_CREDIT_NOTE]: 'table_label__billed_wo_credit_note_spark_chart',
            [BillingStatus.NOT_BILLED]: 'table_label__unbilled_spark_chart',
            [BillingStatus.ORDER_SUM]: 'table_label__order_sum_spark_chart',
        } as const
    )[billing];

    const dateRange = getDateRange(
        window,
        customDateRange ? { from: new Date(customDateRange.from), to: new Date(customDateRange.to) } : null,
    ) ?? {
        from: new Date(),
        to: new Date(),
    };

    const columns: ExplorerColumnDef<T>[] = useMemo(() => {
        const tempCols: ExplorerColumnDef<T>[] = [
            {
                id: 'select',
                cell: ({ row }) =>
                    row.original.id ? (
                        <Button
                            variant="outline"
                            onClick={() => {
                                row.toggleSelected(!row.getIsSelected());
                            }}
                            selected={row.getIsSelected()}
                        >
                            <FunnelIcon className="h-3 w-3" />
                            {t(
                                row.getIsSelected()
                                    ? 'filter_header__delete_button'
                                    : 'filter_header__filter_button',
                                {
                                    ns: 'generic-list',
                                },
                            )}
                        </Button>
                    ) : null,
                enableSorting: false,
                enableHiding: false,
                size: 120,
                meta: {
                    preserveOpacityWhenDimmed: true,
                },
            },
        ];

        tempCols.push(
            ...(config.columns.map((customColumn) => ({
                id: customColumn.id,
                accessorKey: customColumn.accessorKey,
                size: customColumn.size,
                cell: ({ row }) => customColumn.generateCell(row.original),
                header: ({ column }) => (
                    <DataTableSortHeader column={column} title={customColumn.headerText} />
                ),
            })) as ExplorerColumnDef<T>[]),
        );

        if (targetUnit === TargetUnit.REVENUE) {
            tempCols.push(
                {
                    id: 'revenue_current',
                    accessorKey: 'revenue_current',
                    header: ({ column }) => (
                        <DataTableSortHeader
                            align="right"
                            column={column}
                            title={t(periodLabelKey, {
                                period: t(`table_label__period_${window}`, {
                                    from: dateRange.from,
                                    to: dateRange.to,
                                    period: getYear(dateRange.to),
                                }),
                            })}
                        >
                            <MetricInfoHint metric={billing} />
                        </DataTableSortHeader>
                    ),
                    cell: ({ cell }) => (
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>{cell.getValue() as string}</CurrencyOrEmpty>
                        </DataText>
                    ),
                    meta: {
                        align: 'right',
                    },
                    summary: (data) => (
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>{data.revenue_current}</CurrencyOrEmpty>
                        </DataText>
                    ),
                    exporter: [
                        {
                            name: t(periodLabelKey, {
                                period: t(`table_label__period_${window}`, {
                                    from: dateRange.from,
                                    to: dateRange.to,
                                    period: getYear(dateRange.to),
                                }),
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.revenue_current,
                        },
                    ],
                },
                {
                    id: 'revenue_last_period',
                    accessorKey: 'revenue_last_period',
                    header: ({ column }) => (
                        <DataTableSortHeader
                            align="right"
                            column={column}
                            title={t(periodLabelKey, {
                                period: t(`table_label__period_${window}`, {
                                    from: subYears(dateRange.from, 1),
                                    to: subYears(dateRange.to, 1),
                                    period: getYear(subYears(dateRange.to, 1)),
                                }),
                            })}
                        >
                            <MetricInfoHint metric={billing} />
                        </DataTableSortHeader>
                    ),
                    cell: ({ cell }) => (
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>{cell.getValue() as string}</CurrencyOrEmpty>
                        </DataText>
                    ),
                    meta: {
                        align: 'right',
                    },
                    summary: (data) => (
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>{data.revenue_last_period}</CurrencyOrEmpty>
                        </DataText>
                    ),
                    exporter: [
                        {
                            name: t(periodLabelKey, {
                                period: t(`table_label__period_${window}`, {
                                    from: subYears(dateRange.from, 1),
                                    to: subYears(dateRange.to, 1),
                                    period: getYear(subYears(dateRange.to, 1)),
                                }),
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.revenue_last_period,
                        },
                    ],
                },
                {
                    id: 'revenue_delta_amount',
                    accessorKey: 'revenue_delta_amount',
                    header: ({ column }) => (
                        <DataTableSortHeader
                            align="right"
                            column={column}
                            title={t('table_label__revenue_delta_amount', {
                                ns: 'explorer',
                            })}
                        />
                    ),
                    cell: ({ row }) => {
                        return (
                            <DeltaDoubleDeckerCell
                                percentage={row.original.revenue_delta_percentage}
                                amount={row.original.revenue_delta_amount}
                            />
                        );
                    },
                    meta: {
                        align: 'right',
                    },
                    summary: (data) => (
                        <DeltaDoubleDeckerCell
                            percentage={data.revenue_delta_percentage}
                            amount={data.revenue_delta_amount}
                        />
                    ),
                    exporter: [
                        {
                            name: t('table_label__revenue_delta_amount', {
                                defaultValue: 'revenue_delta_amount',
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.revenue_delta_amount,
                        },
                        {
                            name: t('table_label__revenue_delta_percentage', {
                                defaultValue: 'revenue_delta_percentage',
                            }),
                            getXlsxCell: (row: ExplorerTableData) =>
                                row.revenue_delta_percentage === null
                                    ? null
                                    : { v: row.revenue_delta_percentage, t: 'n', z: '0.00%' },
                        },
                    ],
                },
            );

            // Revenue yearPeriods
            const newReneueYearPeriods: ExplorerColumnDef<T>[] = yearPeriodsHelper
                .getIntervals(yearPeriods)
                .map((interval) => {
                    const id = yearPeriodsHelper.getRevenueColumnNameByInterval(interval);

                    return {
                        id,
                        accessorKey: id,
                        header: ({ column }) => (
                            <DataTableSortHeader
                                align="right"
                                column={column}
                                title={t(periodLabelKey, {
                                    period: t(`table_label__period_${window}`, {
                                        from: subYears(dateRange.from, interval),
                                        to: subYears(dateRange.to, interval),
                                        period: getYear(subYears(dateRange.to, interval)),
                                    }),
                                })}
                            >
                                <MetricInfoHint metric={billing} />
                            </DataTableSortHeader>
                        ),
                        cell: ({ cell }) => (
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>{cell.getValue() as string}</CurrencyOrEmpty>
                            </DataText>
                        ),
                        meta: {
                            align: 'right',
                        },
                        summary: (data) => (
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>
                                    {data[id as keyof ExplorerGetExplorerTotalSummary]}
                                </CurrencyOrEmpty>
                            </DataText>
                        ),
                        exporter: [
                            {
                                name: t(periodLabelKey, {
                                    period: t(`table_label__period_${window}`, {
                                        from: subYears(dateRange.from, interval),
                                        to: subYears(dateRange.to, interval),
                                        period: getYear(subYears(dateRange.to, interval)),
                                    }),
                                }),
                                getXlsxCell: (row: ExplorerTableData) =>
                                    row[id as keyof ExplorerTableData] as number,
                            },
                        ],
                    };
                });

            tempCols.push(...newReneueYearPeriods);

            tempCols.push(
                {
                    id: 'revenue_ttm_monthly_sales_over_last_2_years',
                    accessorKey: 'monthly_sales_over_last_2_years',
                    header: t(sparkChartLabelKey, {
                        ns: 'explorer',
                    }),
                    cell: RevenueSparkAreaCell,
                },
                {
                    id: 'revenue_ttm_monthly_orders_over_last_2_years',
                    accessorKey: 'monthly_sales_over_last_2_years',
                    header: t('table_label__order_count_spark_chart', {
                        ns: 'explorer',
                    }),
                    cell: OrderCountSparkBarCell,
                },
                // ----------------- Margin -----------------
                {
                    id: 'gross_margin_current',
                    accessorKey: 'gross_margin_current',
                    header: ({ column }) => (
                        <DataTableSortHeader
                            align="right"
                            column={column}
                            title={
                                <MaskedContent>
                                    {t('table_label__margin_period', {
                                        period: t(`table_label__period_${window}`, {
                                            from: dateRange.from,
                                            to: dateRange.to,
                                            period: getYear(dateRange.to),
                                        }),
                                    })}
                                </MaskedContent>
                            }
                        >
                            <MetricInfoHint base={billing} metric="GROSS_MARGIN" />
                        </DataTableSortHeader>
                    ),
                    cell: ({ cell }) => (
                        <MaskedContent>
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>{cell.getValue() as string}</CurrencyOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    meta: {
                        align: 'right',
                    },
                    summary: (data) => (
                        <MaskedContent>
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>{data.gross_margin_current}</CurrencyOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    exporter: [
                        {
                            name: t('table_label__margin_period', {
                                period: t(`table_label__period_${window}`, {
                                    from: dateRange.from,
                                    to: dateRange.to,
                                    period: getYear(dateRange.to),
                                }),
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.gross_margin_current,
                        },
                    ],
                },
                {
                    id: 'gross_margin_percentage_current',
                    accessorKey: 'gross_margin_percentage_current',
                    header: ({ column }) => (
                        <DataTableSortHeader
                            align="right"
                            column={column}
                            title={
                                <MaskedContent>
                                    {t('table_label__margin_pct_period', {
                                        period: t(`table_label__period_${window}`, {
                                            from: dateRange.from,
                                            to: dateRange.to,
                                            period: getYear(dateRange.to),
                                        }),
                                    })}
                                </MaskedContent>
                            }
                        >
                            <MetricInfoHint base={billing} metric="GROSS_MARGIN_PERCENTAGE" />
                        </DataTableSortHeader>
                    ),
                    cell: ({ cell }) => (
                        <MaskedContent>
                            <DataText>
                                <PercentageOrEmpty showEmptyIfZero>
                                    {cell.getValue() as number}
                                </PercentageOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    meta: {
                        align: 'right',
                    },
                    summary: (data) => (
                        <MaskedContent>
                            <DataText>
                                <PercentageOrEmpty showEmptyIfZero>
                                    {data.gross_margin_percentage_current}
                                </PercentageOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    exporter: [
                        {
                            name: t('table_label__margin_pct_period', {
                                period: t(`table_label__period_${window}`, {
                                    from: dateRange.from,
                                    to: dateRange.to,
                                    period: getYear(dateRange.to),
                                }),
                            }),
                            getXlsxCell: (row: ExplorerTableData) => ({
                                v: row.gross_margin_percentage_current,
                                t: 'n',
                                z: '0.00%',
                            }),
                        },
                    ],
                },
                {
                    id: 'gross_margin_last_period',
                    accessorKey: 'gross_margin_last_period',
                    header: ({ column }) => (
                        <DataTableSortHeader
                            align="right"
                            column={column}
                            title={
                                <MaskedContent>
                                    {t('table_label__margin_period', {
                                        period: t(`table_label__period_${window}`, {
                                            from: subYears(dateRange.from, 1),
                                            to: subYears(dateRange.to, 1),
                                            period: getYear(subYears(dateRange.to, 1)),
                                        }),
                                    })}
                                </MaskedContent>
                            }
                        >
                            <MetricInfoHint base={billing} metric="GROSS_MARGIN" />
                        </DataTableSortHeader>
                    ),
                    cell: ({ cell }) => (
                        <MaskedContent>
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>{cell.getValue() as string}</CurrencyOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    meta: {
                        align: 'right',
                    },
                    summary: (data) => (
                        <MaskedContent>
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>
                                    {data.gross_margin_last_period}
                                </CurrencyOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    exporter: [
                        {
                            name: t('table_label__margin_period', {
                                period: t(`table_label__period_${window}`, {
                                    from: subYears(dateRange.from, 1),
                                    to: subYears(dateRange.to, 1),
                                    period: getYear(subYears(dateRange.to, 1)),
                                }),
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.gross_margin_last_period,
                        },
                    ],
                },
                {
                    id: 'gross_margin_percentage_last_period',
                    accessorKey: 'gross_margin_percentage_last_period',
                    header: ({ column }) => (
                        <DataTableSortHeader
                            align="right"
                            column={column}
                            title={
                                <MaskedContent>
                                    {t('table_label__margin_pct_period', {
                                        period: t(`table_label__period_${window}`, {
                                            from: subYears(dateRange.from, 1),
                                            to: subYears(dateRange.to, 1),
                                            period: getYear(subYears(dateRange.to, 1)),
                                        }),
                                    })}
                                </MaskedContent>
                            }
                        >
                            <MetricInfoHint base={billing} metric="GROSS_MARGIN_PERCENTAGE" />
                        </DataTableSortHeader>
                    ),
                    cell: ({ cell }) => (
                        <MaskedContent>
                            <DataText>
                                <PercentageOrEmpty showEmptyIfZero>
                                    {cell.getValue() as number}
                                </PercentageOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    meta: {
                        align: 'right',
                    },
                    summary: (data) => (
                        <MaskedContent>
                            <DataText>
                                <PercentageOrEmpty showEmptyIfZero>
                                    {data.gross_margin_percentage_last_period}
                                </PercentageOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    exporter: [
                        {
                            name: t('table_label__margin_pct_period', {
                                period: t(`table_label__period_${window}`, {
                                    from: subYears(dateRange.from, 1),
                                    to: subYears(dateRange.to, 1),
                                    period: getYear(subYears(dateRange.to, 1)),
                                }),
                            }),
                            getXlsxCell: (row: ExplorerTableData) => ({
                                v: row.gross_margin_percentage_last_period,
                                t: 'n',
                                z: '0.00%',
                            }),
                        },
                    ],
                },
                {
                    id: 'gross_margin_delta_amount',
                    accessorKey: 'gross_margin_delta_amount',
                    header: ({ column }) => (
                        <DataTableSortHeader
                            align="right"
                            column={column}
                            title={
                                <MaskedContent>
                                    {t('table_label__gross_margin_delta_amount', {
                                        ns: 'explorer',
                                    })}
                                </MaskedContent>
                            }
                        />
                    ),
                    cell: ({ row }) => {
                        return (
                            <DeltaDoubleDeckerCell
                                percentage={row.original.gross_margin_delta_percentage}
                                amount={row.original.gross_margin_delta_amount}
                                masked={true}
                            />
                        );
                    },
                    meta: {
                        align: 'right',
                    },
                    summary: (data) => (
                        <DeltaDoubleDeckerCell
                            percentage={data.gross_margin_delta_percentage}
                            amount={data.gross_margin_delta_amount}
                            masked
                        />
                    ),
                    exporter: [
                        {
                            name: t('table_label__gross_margin_delta_amount', {
                                defaultValue: 'gross_margin_delta_amount',
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.gross_margin_delta_amount,
                        },
                        {
                            name: t('table_label__gross_margin_delta_percentage', {
                                defaultValue: 'gross_margin_delta_percentage',
                            }),
                            getXlsxCell: (row: ExplorerTableData) =>
                                row.gross_margin_delta_percentage === null
                                    ? null
                                    : { v: row.gross_margin_delta_percentage, t: 'n', z: '0.00%' },
                        },
                    ],
                },
            );

            // Margin yearPeriods with GM % next to each GM column
            const newMarginYearPeriods: ExplorerColumnDef<T>[] = yearPeriodsHelper
                .getIntervals(yearPeriods)
                .flatMap((interval) => {
                    const marginId = yearPeriodsHelper.getGrossMarginColumnNameByInterval(interval);
                    const marginPctId =
                        yearPeriodsHelper.getGrossMarginPercentageColumnNameByInterval(interval);

                    const marginColumn: ExplorerColumnDef<T> = {
                        id: marginId,
                        accessorKey: marginId,
                        header: ({ column }) => (
                            <DataTableSortHeader
                                align="right"
                                column={column}
                                title={
                                    <MaskedContent>
                                        {t('table_label__margin_period', {
                                            period: t(`table_label__period_${window}`, {
                                                from: subYears(dateRange.from, interval),
                                                to: subYears(dateRange.to, interval),
                                                period: getYear(subYears(dateRange.to, interval)),
                                            }),
                                        })}
                                    </MaskedContent>
                                }
                            >
                                <MetricInfoHint base={billing} metric="GROSS_MARGIN" />
                            </DataTableSortHeader>
                        ),
                        cell: ({ cell }) => (
                            <MaskedContent>
                                <DataText>
                                    <CurrencyOrEmpty showEmptyIfZero>
                                        {cell.getValue() as string}
                                    </CurrencyOrEmpty>
                                </DataText>
                            </MaskedContent>
                        ),
                        meta: {
                            align: 'right',
                        },
                        summary: (data) => (
                            <MaskedContent>
                                <DataText>
                                    <CurrencyOrEmpty showEmptyIfZero>
                                        {data[marginId as keyof ExplorerGetExplorerTotalSummary]}
                                    </CurrencyOrEmpty>
                                </DataText>
                            </MaskedContent>
                        ),
                        exporter: [
                            {
                                name: t('table_label__margin_period', {
                                    period: t(`table_label__period_${window}`, {
                                        from: subYears(dateRange.from, interval),
                                        to: subYears(dateRange.to, interval),
                                        period: getYear(subYears(dateRange.to, interval)),
                                    }),
                                }),
                                getXlsxCell: (row: ExplorerTableData) =>
                                    row[marginId as keyof ExplorerTableData] as number,
                            },
                        ],
                    };

                    const marginPctColumn: ExplorerColumnDef<T> = {
                        id: marginPctId,
                        accessorKey: marginPctId,
                        header: ({ column }) => (
                            <DataTableSortHeader
                                align="right"
                                column={column}
                                title={
                                    <MaskedContent>
                                        {t('table_label__margin_pct_period', {
                                            period: t(`table_label__period_${window}`, {
                                                from: subYears(dateRange.from, interval),
                                                to: subYears(dateRange.to, interval),
                                                period: getYear(subYears(dateRange.to, interval)),
                                            }),
                                        })}
                                    </MaskedContent>
                                }
                            >
                                <MetricInfoHint base={billing} metric="GROSS_MARGIN_PERCENTAGE" />
                            </DataTableSortHeader>
                        ),
                        cell: ({ cell }) => (
                            <MaskedContent>
                                <DataText>
                                    <PercentageOrEmpty showEmptyIfZero>
                                        {cell.getValue() as number}
                                    </PercentageOrEmpty>
                                </DataText>
                            </MaskedContent>
                        ),
                        meta: {
                            align: 'right',
                        },
                        summary: (data) => (
                            <MaskedContent>
                                <DataText>
                                    <PercentageOrEmpty showEmptyIfZero>
                                        {data[marginPctId as keyof ExplorerGetExplorerTotalSummary]}
                                    </PercentageOrEmpty>
                                </DataText>
                            </MaskedContent>
                        ),
                        exporter: [
                            {
                                name: t('table_label__margin_pct_period', {
                                    period: t(`table_label__period_${window}`, {
                                        from: subYears(dateRange.from, interval),
                                        to: subYears(dateRange.to, interval),
                                        period: getYear(subYears(dateRange.to, interval)),
                                    }),
                                }),
                                getXlsxCell: (row: ExplorerTableData) => ({
                                    v: row[marginPctId],
                                    t: 'n',
                                    z: '0.00%',
                                }),
                            },
                        ],
                    };

                    return [marginColumn, marginPctColumn];
                });

            tempCols.push(...newMarginYearPeriods);

            // --- end
        } else {
            tempCols.push(
                {
                    id: 'units_current',
                    accessorKey: 'units_current',
                    header: ({ column }) => (
                        <DataTableSortHeader
                            align="right"
                            column={column}
                            title={t('table_label__units_current', {
                                ns: 'explorer',
                            })}
                        >
                            <MetricInfoHint metric="UNITS" />
                        </DataTableSortHeader>
                    ),
                    cell: ({ cell }) => (
                        <DataText>
                            <NumberOrEmpty showEmptyIfZero>{cell.getValue() as string}</NumberOrEmpty>
                        </DataText>
                    ),
                    meta: {
                        align: 'right',
                    },
                    summary: (data) => (
                        <DataText>
                            <NumberOrEmpty showEmptyIfZero>{data.units_current}</NumberOrEmpty>
                        </DataText>
                    ),
                    exporter: [
                        {
                            name: t('table_label__units_current', { defaultValue: 'units_current' }),
                            getXlsxCell: (row: ExplorerTableData) => row.units_current,
                        },
                    ],
                },
                {
                    id: 'units_last_period',
                    accessorKey: 'units_last_period',
                    header: ({ column }) => (
                        <DataTableSortHeader
                            align="right"
                            column={column}
                            title={t('table_label__units_last_period', {
                                ns: 'explorer',
                            })}
                        >
                            <MetricInfoHint metric="UNITS" />
                        </DataTableSortHeader>
                    ),
                    cell: ({ cell }) => (
                        <DataText>
                            <NumberOrEmpty showEmptyIfZero>{cell.getValue() as string}</NumberOrEmpty>
                        </DataText>
                    ),
                    meta: {
                        align: 'right',
                    },
                    summary: (data) => (
                        <DataText>
                            <NumberOrEmpty showEmptyIfZero>{data.units_last_period}</NumberOrEmpty>
                        </DataText>
                    ),
                    exporter: [
                        {
                            name: t('table_label__units_last_period', {
                                defaultValue: 'units_last_period',
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.units_last_period,
                        },
                    ],
                },
                {
                    id: 'units_delta_amount',
                    accessorKey: 'units_delta_amount',
                    header: ({ column }) => (
                        <DataTableSortHeader
                            align="right"
                            column={column}
                            title={t('table_label__units_delta_amount', {
                                ns: 'explorer',
                            })}
                        />
                    ),
                    cell: ({ row }) => {
                        return (
                            <DeltaDoubleDeckerCell
                                percentage={row.original.units_delta_percentage}
                                amount={row.original.units_delta_amount}
                                style="number"
                            />
                        );
                    },
                    meta: {
                        align: 'right',
                    },
                    summary: (data) => (
                        <DeltaDoubleDeckerCell
                            percentage={data.units_delta_percentage}
                            amount={data.units_delta_amount}
                            style="number"
                        />
                    ),
                    exporter: [
                        {
                            name: t('table_label__units_delta_amount', {
                                defaultValue: 'units_delta_amount',
                            }),
                            getXlsxCell: (row: ExplorerTableData) => row.units_delta_amount,
                        },
                        {
                            name: t('table_label__units_delta_percentage', {
                                defaultValue: 'units_delta_percentage',
                            }),
                            getXlsxCell: (row: ExplorerTableData) =>
                                row.units_delta_percentage === null
                                    ? null
                                    : { v: row.units_delta_percentage, t: 'n', z: '0.00%' },
                        },
                    ],
                },
                {
                    id: 'units_ttm_monthly_sales_over_last_2_years',
                    accessorKey: 'units_ttm_monthly_sales_over_last_2_years',
                    header: t('table_label__units_spark_chart', {
                        ns: 'explorer',
                    }),
                    cell: UnitsSparkAreaCell,
                },
                {
                    id: 'units_ttm_monthly_count_over_last_2_years',
                    accessorKey: 'monthly_sales_over_last_2_years',
                    header: t('table_label__order_count_spark_chart', {
                        ns: 'explorer',
                    }),
                    cell: OrderCountSparkBarCell,
                },
            );
        }

        return tempCols.filter((col) => acl.can('read', 'SalesReport', col.id as never));
    }, [
        //
        acl,
        billing,
        config.columns,
        t,
        targetUnit,
        periodLabelKey,
        sparkChartLabelKey,
        window,
        yearPeriods,
        dateRange.from,
        dateRange.to,
    ]);

    return columns;
}
